import React from "react";
import { useTranslation } from "react-i18next";
import { endOfDay, startOfDay } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type Column } from "@/components/ui/data-table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageState } from "@/components/common/PageState";
import { formatDateTime } from "@/utils/dateUtils";
import type {
  PartnerOrderEarningState,
  PartnerOrderRecord,
} from "@/types/partner-order";
import { describeError } from "./utils/describeError";
import {
  useAdminPartnerOrders,
  useAdminPartnerOrdersSummary,
} from "./hooks/partner-revenue-summary.hooks";
import { PartnerPeriodSelector } from "@/components/pages/guesteria-partners/components/PartnerPeriodSelector";
import { usePartnerRevenuePeriod } from "@/components/pages/guesteria-partners/hooks/usePartnerRevenuePeriod";

const ORDERS_PAGE_LIMIT = 20;

// Null-safe: a missing/malformed value must render as "—", never throw
// (the same white-screen class the Cities pages guard against — see
// `describeError.ts`).
function formatMoney(
  value: number | null | undefined,
  currency: string | undefined,
): string {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }
  let symbol = "";
  if (currency === "EUR") {
    symbol = "€";
  } else if (currency) {
    symbol = `${currency} `;
  }
  return `${symbol}${value.toLocaleString("en-GB")}`;
}

/** Task 0031, contract 2026-09-29 — same tooltip/marker as `PartnerOrdersTable.tsx`. */
function EstimatedMoneyCell({
  value,
  currency,
  estimated,
  t,
}: {
  value: number | null | undefined;
  currency: string | undefined;
  estimated: boolean;
  t: (key: string) => string;
}): React.JSX.Element {
  const formatted = formatMoney(value, currency);
  if (!estimated || formatted === "—") {
    return <span>{formatted}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted">
          ≈ {formatted}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {t("billing.partnerOrders.estimatedTooltip")}
      </TooltipContent>
    </Tooltip>
  );
}

/** Task 0033, contract 2026-09-29 — same badge/variant choice as `PartnerOrdersTable.tsx`. */
function EarningStateBadge({
  state,
  t,
}: {
  state: PartnerOrderEarningState;
  t: (key: string) => string;
}): React.JSX.Element {
  let variant: "success" | "destructive" | "outline" = "outline";
  if (state === "earned") {
    variant = "success";
  } else if (state === "cancelled") {
    variant = "destructive";
  }
  return (
    <Badge variant={variant}>
      {t(`billing.partnerOrders.earningStateValues.${state}`)}
    </Badge>
  );
}

const StatTile: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-muted-foreground text-sm font-normal">
        {label}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <span className="text-foreground text-2xl font-semibold">{value}</span>
    </CardContent>
  </Card>
);

export const PartnerRevenueSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { range } = usePartnerRevenuePeriod();
  const [page, setPage] = React.useState(1);

  const from = startOfDay(range.from).toISOString();
  const to = endOfDay(range.to).toISOString();

  React.useEffect(() => {
    setPage(1);
  }, [from, to]);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    error: summaryError,
  } = useAdminPartnerOrdersSummary({ from, to });

  const {
    data: ordersResponse,
    isLoading: isOrdersLoading,
    isError: isOrdersError,
    error: ordersError,
  } = useAdminPartnerOrders({
    from,
    to,
    page,
    limit: ORDERS_PAGE_LIMIT,
    catalog: "all",
  });

  const columns: Column<PartnerOrderRecord>[] = [
    {
      key: "travelDate",
      header: t("billing.partnerOrders.columns.serviceDate"),
      render: (value: string) => formatDateTime(value),
    },
    {
      key: "bookingDate",
      header: t("billing.partnerOrders.columns.bookedOn"),
      render: (value: string) => formatDateTime(value),
    },
    {
      key: "hotelName",
      header: t("partners.admin.summary.columns.hotel"),
      render: (value: string | undefined, row) => value || row.hotelId || "—",
    },
    {
      key: "partnerProductName",
      header: t("partners.admin.summary.columns.product"),
      className: "min-w-[200px] whitespace-normal",
    },
    {
      key: "roomNumber",
      header: t("billing.partnerOrders.columns.room"),
      render: (_value: string | null | undefined, row: PartnerOrderRecord) => {
        if (row.attributionSource === "hotel_wide") {
          return <span>{t("billing.partnerOrders.receptionLabel")}</span>;
        }
        if (row.roomNumber) {
          return <span>{row.roomNumber}</span>;
        }
        if (row.attributionSource === "room") {
          return (
            <span className="text-muted-foreground">
              {t("partners.detail.attribution.unknownRoomLabel")}
            </span>
          );
        }
        return <span>—</span>;
      },
    },
    {
      key: "travellers",
      header: t("billing.partnerOrders.columns.guests"),
      render: (value: number) => value,
    },
    {
      key: "earningState",
      header: t("billing.partnerOrders.columns.earningState"),
      render: (value: PartnerOrderEarningState) => (
        <EarningStateBadge state={value} t={t} />
      ),
    },
    {
      key: "totalGuestPrice",
      header: t("partners.admin.summary.columns.guestPrice"),
      render: (value: number | null | undefined, row) => (
        <EstimatedMoneyCell
          value={value}
          currency={summary?.currency}
          estimated={Boolean(row.amountEstimated)}
          t={t}
        />
      ),
    },
    {
      key: "hotelNetPrice",
      header: t("partners.admin.summary.columns.netPrice"),
      render: (value: number | null | undefined, row) => (
        <EstimatedMoneyCell
          value={value}
          currency={summary?.currency}
          estimated={Boolean(row.amountEstimated)}
          t={t}
        />
      ),
    },
    {
      key: "createdAt",
      header: t("partners.admin.summary.columns.createdAt"),
      render: (value: string) => formatDateTime(value),
    },
    {
      key: "outOfCatalog",
      header: t("partners.admin.summary.columns.outOfCatalog"),
      render: (value: boolean) =>
        value ? (
          <Badge variant="outline">
            {t("partners.admin.summary.columns.outOfCatalog")}
          </Badge>
        ) : null,
    },
    {
      key: "providerCommissionAmount",
      header: t("billing.partnerOrders.columns.providerCommission"),
      // Not marked by `amountEstimated` — that flag covers the guest/hotel
      // amounts, not the Viator reconciliation figure — so this renders as
      // a plain value, no "≈" marker.
      render: (value: number | null | undefined, row) =>
        formatMoney(value, row.providerCommissionCurrency),
    },
    {
      key: "reportImportedAt",
      header: t("billing.partnerOrders.columns.reportImportedAt"),
      render: (value: string | null | undefined) =>
        value ? formatDateTime(value) : "—",
    },
  ];

  if (isSummaryLoading) {
    return <PageState text={t("partners.admin.summary.loading")} />;
  }

  if (isSummaryError) {
    return (
      <PageState
        text={describeError(
          t("partners.admin.summary.loadError"),
          summaryError,
        )}
        tone="error"
      />
    );
  }

  const totalPages = Math.max(
    1,
    Math.ceil((ordersResponse?.total ?? 0) / ORDERS_PAGE_LIMIT),
  );

  return (
    <div className="mx-auto flex flex-col gap-6 p-12">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-foreground text-2xl font-semibold">
            {t("partners.admin.summary.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.summary.description")}
          </p>
        </div>
        <PartnerPeriodSelector />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label={t("partners.admin.summary.totalOrders")}
          value={summary?.totalOrders ?? "—"}
        />
        <StatTile
          label={t("partners.admin.summary.cancelledOrders")}
          value={summary?.cancelledOrders ?? "—"}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-help">
              <StatTile
                label={t("partners.admin.summary.estimatedOrders")}
                value={summary?.estimatedOrders ?? "—"}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {t("billing.partnerOrders.estimatedTooltip")}
          </TooltipContent>
        </Tooltip>
        <StatTile
          label={t("partners.admin.summary.totalRevenue")}
          value={formatMoney(summary?.totalRevenue, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.totalBooked")}
          value={formatMoney(summary?.totalBooked, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.earnings")}
          value={formatMoney(summary?.earnings, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.pendingEarnings")}
          value={formatMoney(summary?.pendingEarnings, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.guesteriaCommission")}
          value={formatMoney(summary?.guesteriaCommission, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.guesteriaNet")}
          value={formatMoney(summary?.guesteriaNet, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.pendingGuesteriaCommission")}
          value={formatMoney(
            summary?.pendingGuesteriaCommission,
            summary?.currency,
          )}
        />
        <StatTile
          label={t("partners.admin.summary.pendingGuesteriaNet")}
          value={formatMoney(summary?.pendingGuesteriaNet, summary?.currency)}
        />
        <StatTile
          label={t("partners.admin.summary.currency")}
          value={summary?.currency ?? "—"}
        />
      </div>

      {summary && (
        <div className="flex flex-col gap-1">
          <h2 className="text-foreground text-lg font-semibold">
            {t("partners.admin.summary.outOfCatalogTitle")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.summary.outOfCatalogValue", {
              count: summary.outOfCatalog.count,
              revenue: formatMoney(
                summary.outOfCatalog.revenue,
                summary.currency,
              ),
              commission: formatMoney(
                summary.outOfCatalog.commission,
                summary.currency,
              ),
            })}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <h2 className="text-foreground text-lg font-semibold">
          {t("partners.admin.summary.tableTitle")}
        </h2>
        {isOrdersError ? (
          <PageState
            text={describeError(
              t("partners.admin.summary.loadError"),
              ordersError,
            )}
            tone="error"
          />
        ) : (
          <DataTable
            data={ordersResponse?.data ?? []}
            columns={columns}
            loading={isOrdersLoading}
            getRowKey={(row) => row.id}
            emptyMessage={t("partners.admin.summary.tableEmpty")}
            withSorting={false}
            pagination={{
              currentPage: page,
              totalPages,
              onPageChange: setPage,
            }}
          />
        )}
      </div>
    </div>
  );
};
